import React, { CSSProperties } from 'react';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import { SvgIconProps } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import * as NavigationMenu from '@radix-ui/react-navigation-menu';
import palette from '@ui/palette';
import Icon, { IconType } from '@ui/Icon';
import Typography from '@ui/Typography';
import Tooltip from 'components/Tooltip';

const useStyles = makeStyles()(theme => ({
  tabItem: {
    cursor: 'pointer',
    borderRadius: '4px',
    textWrap: 'nowrap',
    '&:has(:focus)': {
      ...(theme.mixins.focusOutline as CSSProperties),
      outlineOffset: -1.5,
    },
    '&:has(:focus-visible)': {
      ...(theme.mixins.focusOutline as CSSProperties),
      outlineOffset: -1.5,
    },
    '&:hover': {
      backgroundColor: palette.gray_700,
    },
  },
  link: {
    color: palette.gray_0,
    display: 'flex',
    justifyItems: 'stretch',
    justifyContent: 'start',
    gap: 8,
    padding: 8,
    paddingLeft: 12,
    borderRadius: '4px',
    '&:focus': {
      outline: 'none',
    },
    '&[data-active]': {
      backgroundColor: palette.gray_700,
      '&:hover': {
        backgroundColor: palette.gray_900,
      },
    },
  },
  label: {
    opacity: 1,
    transition: theme.transitions.create(['opacity'], {
      easing: theme.transitions.easing.easeIn,
      delay: theme.transitions.duration.shortest,
    }),
  },
  hideLabel: {
    opacity: 0,
  },
  tooltip: {
    textTransform: 'capitalize',
  },
  externalLinkIcon: {
    alignSelf: 'center',
  },
}));

interface NavigationTabProps {
  label: string;
  href: string;
  Icon: React.ElementType<SvgIconProps>;
  isLeftRailOpen: boolean;
  target?: string;
  additionalRoutes?: string[];
  disableLink?: boolean;
}

const NavigationTab = ({
  label,
  Icon: TabIcon,
  href,
  isLeftRailOpen,
  additionalRoutes,
  target = '_self',
  disableLink = false,
}: NavigationTabProps) => {
  const { classes, cx } = useStyles();
  const router = useRouter();
  const pathname = router?.pathname;

  const isExternalLink = target === '_blank';

  const isActive = React.useMemo(() => {
    if (pathname?.includes(href)) return true;
    if (additionalRoutes) {
      return Boolean(additionalRoutes.find(route => pathname?.includes(route)));
    }
    return false;
  }, [additionalRoutes, href, pathname]);

  const content = (
    <>
      <TabIcon fontSize="inherit" />
      <Typography
        variant="pMd"
        color="primary"
        className={cx(classes.label, {
          [classes.hideLabel]: !isLeftRailOpen,
        })}
      >
        {label}
      </Typography>
      {isExternalLink && (
        <Icon
          className={classes.externalLinkIcon}
          type={IconType.openInNew}
          fontSize="small"
        />
      )}
    </>
  );

  return (
    <Tooltip
      text={label}
      classes={{ tooltip: classes.tooltip }}
      placement="right-end"
      PopperProps={{
        hidden: isLeftRailOpen,
      }}
    >
      <NavigationMenu.Item
        key={label}
        aria-label={isExternalLink ? `${label} (opens in new tab)` : label}
        className={cx(classes.tabItem)}
      >
        {disableLink ? (
          <NavigationMenu.Link
            active={isActive}
            className={classes.link}
            style={{ textDecoration: 'none' }}
            href="#"
            onClick={event => event.preventDefault()}
          >
            {content}
          </NavigationMenu.Link>
        ) : (
          <NavigationMenu.Link
            active={isActive}
            className={classes.link}
            style={{ textDecoration: 'none' }}
            asChild
            target={target}
          >
            <NextLink href={href} target={target}>
              {content}
            </NextLink>
          </NavigationMenu.Link>
        )}
      </NavigationMenu.Item>
    </Tooltip>
  );
};

export default NavigationTab;
